/* assets/of-order-lookup-core.js (added 2026-08-01, guest order lookup)
 *
 * Pure core for /pages/track-order. No DOM, no network, no side effects at
 * load. Dual exported (window.OFOrderLookupCore in the browser,
 * module.exports in node) exactly like of-planner-core.js, so every decision
 * here is covered by tests/order-lookup-core.test.mjs.
 *
 * MIRROR NOTE 1: classifyOrderNumber is a deliberate mirror of the same
 * function in /Users/serveradmin/of-insights/app/lib/order-lookup.ts. The
 * client classifies so an S or R number gets the call-us answer with ZERO
 * lookup requests (grill Q2); the server classifies so the outcome log carries
 * a prefix. Change one, change the other.
 *
 * MIRROR NOTE 2: PREFILL_MATCH_SOURCE and PREFILL_PATTERN_SOURCE are mirrors
 * of the inline capture block in layout/theme.liquid, which cannot import this
 * file because it has to run synchronously BEFORE content_for_header.
 * tests/order-lookup-wiring.test.mjs asserts the two have not drifted.
 *
 * The controller reads window.__ofOrderPrefill and NEVER location.search: by
 * the time it runs, the query string has already been scrubbed.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.OFOrderLookupCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var PHONE = '(800) 772-3456';
  var PHONE_HREF = 'tel:8007723456';
  var EMAIL = 'info@ottofrei.com';
  // Footer wording, verbatim, so a customer reaching for the phone reads one
  // consistent line wherever they are (Rhett 2026-08-01).
  var HOURS = 'phone hours: Mon - Fri 8:30am - 5:00pm PST';

  /* Every string the page can say. Kept here, in one object, so the copy is
   * testable and so no branch can invent its own wording. The browser owns
   * the copy: the endpoint returns a reason code and nothing else. */
  var COPY = {
    no_match:
      'We could not find an order matching that number and email. Check both against your order confirmation email, or call us at ' +
      PHONE + ', ' + HOURS + '.',
    rate_limited:
      'We have had a lot of tries on that order recently. Give it a little while and try again, or call us at ' +
      PHONE + ', ' + HOURS + '.',
    challenge_failed:
      'We could not run the security check just now. Try again in a moment, or call us at ' + PHONE + ', ' + HOURS + '.',
    unavailable:
      'We could not look that up just now. Try again in a moment, or call us at ' + PHONE + ', ' + HOURS + '.',
    bad_request:
      'Check the order number and the email and try again. Order numbers start with WS, for example WS71251.',
    phone_order_prefix:
      'Order numbers starting with S or R are phone or counter orders and are not in our online system. Call us at ' +
      PHONE + ', ' + HOURS + ', and we will look it up for you.',
    /* Never says WHY. We cannot distinguish a backorder from hazardous
     * batching from a carrier pickup that missed, and a guess printed in our
     * typeface reads as a fact.
     *
     * DATA LIMIT, stated rather than worked around: the approved mockup reads
     * "It has been 10 days." The endpoint returns aging_nudge as a BOOLEAN and
     * no elapsed-day count, so that sentence is not rendered. Adding it would
     * mean either a new API field or a browser-side subtraction from placed_on,
     * which answers a different question (days since the ORDER, not days the
     * remainder has been sitting) and can be off by one across timezones. */
    aging_nudge_lead: 'This one is taking longer than we like.',
    aging_nudge_rest:
      'Reach out and a person will chase it down for you, we will already know which order this is about.',
    aging_nudge_email: 'Email us about this order',
    call_action: 'Call ' + PHONE,

    /* Tracking. A carrier with no number yet is the common Shopify shape in
     * the first hours after a fulfillment is created; it is a wait, not a
     * fault, and the copy says so without promising a time we do not control
     * beyond what the carriers actually do. */
    tracking_pending:
      'Tracking number is on the way. It usually appears within a business day of shipping.',

    /* The answer sentence, computed by answerSentence() from the model and
     * from nothing else. Every branch below is exercised in the core tests. */
    answer_cancelled: 'This order was cancelled.',
    answer_received: 'We have your order.',
    answer_on_the_way: 'Your order is on the way.',
    answer_not_shipped: 'Your order has not shipped yet.',

    preparing_head: 'Still preparing',
    preparing_sub_split: 'Ships separately. We will send tracking when it moves.',
    preparing_sub_only: 'We will send tracking when it moves.',

    contact_lead: 'Questions?',

    /* Failure, first attempt: calm. The three checks are the ones that
     * actually resolve a failed lookup, in the order they resolve it. */
    fail_head: "We couldn't match that combination.",
    fail_lead_calm: 'Worth a quick check:',
    fail_lead_loud: 'Same checks as before, and if it still will not match, stop typing and let a person do it:',
    fail_checks: [
      [{ text: 'The email should be the one used at checkout, which is not always the everyday one.' }],
      [{ text: 'The order number is in your confirmation email, letters included.' }],
      [
        { text: 'Orders starting with ' },
        { text: 'S', strong: true },
        { text: ' or ' },
        { text: 'R', strong: true },
        { text: ' were placed with our team directly. Give us a call and we will pull it up.' },
      ],
    ],
    /* Failure, second consecutive attempt: the rescue. Typing again is not
     * working, so the page stops asking for it. */
    rescue_lead: "Let's get you an answer the direct way.",
    rescue_rest: 'Call or email and we will look it up on our side in under a minute.',
    rescue_email: 'Email us your order number',

    items_truncated: 'Showing the first 100 items. Call us for the full list.',
    shipments_truncated: 'This order has more shipments than we can show here. Call us for the full history.',
    tracking_truncated: 'This shipment may carry more tracking numbers than we can show here. Call us for the full list.',
    not_yet_shipped_incomplete:
      'This list may be incomplete, because we could not show every shipment on this order. Call us for the full history.',
  };

  // ---- order number ----------------------------------------------------

  function normalizeOrderNumber(raw) {
    if (typeof raw !== 'string') return '';
    return raw.replace(/\s+/g, '').replace(/^#+/, '').toUpperCase();
  }

  function classifyOrderNumber(raw) {
    var value = normalizeOrderNumber(raw);
    if (value === '') return { kind: 'empty', prefix: '', value: '' };
    if (/^WS\d+$/.test(value)) return { kind: 'shopify', prefix: 'WS', value: value };
    if (/^S\d+$/.test(value)) return { kind: 'phone', prefix: 'S', value: value };
    if (/^R\d+$/.test(value)) return { kind: 'phone', prefix: 'R', value: value };
    /* NOT rewritten into a WS guess: silently retrying a bare number as
     * WS<digits> would double the guess budget of every submission, which is
     * exactly the budget the per-order cap exists to hold (spec 2.4). The
     * field hint carries the fix instead. */
    return { kind: 'other', prefix: 'OTHER', value: value };
  }

  var EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

  function isValidEmailShape(raw) {
    if (typeof raw !== 'string') return false;
    var email = raw.trim().toLowerCase();
    return email.length > 0 && email.length <= 254 && EMAIL_RE.test(email);
  }

  // ---- prefill ---------------------------------------------------------

  var PREFILL_MATCH_SOURCE = '[?&]order=([^&#]*)';
  /* Optional leading '#' plus the same body classifyOrderNumber accepts. The
   * prefill value is kept AS TYPED (including a leading '#') rather than
   * normalized here, because normalization is classifyOrderNumber's job, not
   * this validator's; this only decides whether the value is safe to place in
   * the input field at all. */
  var PREFILL_PATTERN_SOURCE = '^#?[A-Z0-9-]{1,32}$';

  /** The validated ?order= value, or null. An invalid value is DROPPED, not
   *  echoed. Mirrors the inline block in layout/theme.liquid exactly. */
  function readPrefillParam(search) {
    try {
      var m = new RegExp(PREFILL_MATCH_SOURCE).exec(String(search == null ? '' : search));
      if (!m) return null;
      var raw = decodeURIComponent(m[1] || '').trim().toUpperCase();
      return new RegExp(PREFILL_PATTERN_SOURCE).test(raw) ? raw : null;
    } catch (e) {
      return null;
    }
  }

  // ---- render model ----------------------------------------------------

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];

  /** A plain YYYY-MM-DD, formatted without ever constructing a Date, so a
   *  browser east of the shop cannot render the day before. */
  function formatDay(day) {
    if (typeof day !== 'string') return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!m) return '';
    var monthIndex = Number(m[2]) - 1;
    if (monthIndex < 0 || monthIndex > 11) return '';
    return MONTHS[monthIndex] + ' ' + Number(m[3]) + ', ' + m[1];
  }

  var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul',
    'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** The card head form: "Jul 30". The year is appended ONLY when the day
   *  falls outside currentYear, so an old order never reads as a recent one
   *  while the everyday case stays short. Same no-Date parsing as formatDay:
   *  a browser east of the shop must not render the day before. */
  function formatDayShort(day, currentYear) {
    if (typeof day !== 'string') return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!m) return '';
    var monthIndex = Number(m[2]) - 1;
    if (monthIndex < 0 || monthIndex > 11) return '';
    var label = MONTHS_SHORT[monthIndex] + ' ' + Number(m[3]);
    if (typeof currentYear === 'number' && currentYear !== Number(m[1])) label += ', ' + m[1];
    return label;
  }

  /* ---- carrier tracking URLs ------------------------------------------
   * Preference order, fixed: (a) the url the endpoint returned, (b) the
   * canonical page for the four carriers below, built from the number,
   * (c) plain text. There is no (d). A guessed URL for a carrier we do not
   * have a template for sends a customer to a dead page in our name, which is
   * worse than a number they can paste themselves.
   */
  var CARRIER_URLS = {
    UPS: 'https://www.ups.com/track?loc=en_US&tracknum=',
    USPS: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
    FEDEX: 'https://www.fedex.com/fedextrack/?trknbr=',
    DHL: 'https://www.dhl.com/us-en/home/tracking.html?tracking-id=',
  };

  /** 'FedEx Ground', 'ups', 'UPS® 2nd Day Air', 'United States Postal
   *  Service' all resolve; anything else returns ''. USPS is tested BEFORE
   *  UPS on purpose: the string "usps" contains "ups". */
  function normalizeCarrier(raw) {
    if (typeof raw !== 'string') return '';
    var spaced = raw.toLowerCase().replace(/[^a-z]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!spaced) return '';
    var flat = spaced.replace(/ /g, '');
    var words = spaced.split(' ');
    function has(word) { return words.indexOf(word) !== -1; }
    if (has('usps') || flat.indexOf('unitedstatespostal') !== -1 || flat.indexOf('postalservice') !== -1) return 'USPS';
    if (has('dhl') || flat.indexOf('dhl') === 0) return 'DHL';
    if (flat.indexOf('fedex') !== -1 || flat.indexOf('federalexpress') !== -1) return 'FEDEX';
    if (has('ups') || flat.indexOf('ups') === 0 || flat.indexOf('unitedparcel') !== -1) return 'UPS';
    return '';
  }

  /** The canonical carrier tracking page for a number, or null. Null for an
   *  unknown carrier, null for a missing number, null for a missing carrier. */
  function carrierTrackingUrl(carrier, number) {
    var key = normalizeCarrier(carrier);
    if (!key || !CARRIER_URLS[key]) return null;
    var value = typeof number === 'string' ? number.trim() : '';
    if (!value) return null;
    return CARRIER_URLS[key] + encodeURIComponent(value);
  }

  function items(list) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (item) {
      if (!item || typeof item.name !== 'string' || !item.name) return;
      out.push({ name: item.name, quantity: Number(item.quantity) || 0 });
    });
    return out;
  }

  /* A row is kept when it carries a number OR a carrier. The carrier-only row
   * is the shape Shopify produces in the first hours after a fulfillment is
   * created, and dropping it (as this did before the card redesign) threw away
   * the one true thing we could say: who has it. */
  function tracking(list) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (row) {
      if (!row || typeof row !== 'object') return;
      var carrier = typeof row.carrier === 'string' && row.carrier ? row.carrier : null;
      var number = typeof row.number === 'string' && row.number ? row.number : null;
      if (!carrier && !number) return;
      // The endpoint already emitted null for anything that is not https.
      var url = typeof row.url === 'string' && row.url ? row.url : null;
      // Preference (b): only ever consulted when the endpoint gave us none.
      if (!url && number) url = carrierTrackingUrl(carrier, number);
      out.push({ carrier: carrier, number: number, url: url });
    });
    return out;
  }

  function itemCount(n) { return n === 1 ? '1 item' : n + ' items'; }

  /** The card's lead line, computed from the model and from nothing else.
   *  Every branch is exercised in tests/order-lookup-core.test.mjs: 0, 1 and
   *  many shipments, each with and without a preparing remainder, plus the
   *  cancelled and the truncated cases where a count would be a lie. */
  function answerSentence(model) {
    var shipped = model && Array.isArray(model.shipments) ? model.shipments.length : 0;
    var waiting = model && Array.isArray(model.notYetShipped) ? model.notYetShipped.length : 0;

    if (model && model.status === 'cancelled') return { headline: COPY.answer_cancelled, sub: null };

    var sub = null;
    if (waiting) {
      sub = shipped
        ? itemCount(waiting) + (waiting === 1 ? ' is' : ' are') + ' still preparing and ship' +
          (waiting === 1 ? 's' : '') + ' separately.'
        : itemCount(waiting) + (waiting === 1 ? ' is' : ' are') + ' still preparing. ' + COPY.preparing_sub_only;
    }

    if (!shipped) return { headline: waiting ? COPY.answer_not_shipped : COPY.answer_received, sub: sub };

    /* A truncated shipment list makes "N of M" false, so the count is not
     * spoken at all. The truncation notice carries the rest. */
    if (model && model.shipmentsTruncated) return { headline: COPY.answer_on_the_way, sub: sub };

    if (!waiting) {
      return {
        headline: shipped === 1 ? COPY.answer_on_the_way : 'All ' + shipped + ' shipments are on the way.',
        sub: null,
      };
    }
    // The remainder is counted as the one shipment that has not left yet.
    var total = shipped + 1;
    return {
      headline: shipped + ' of ' + total + ' shipments ' + (shipped === 1 ? 'is' : 'are') + ' on the way.',
      sub: sub,
    };
  }

  /** Failure escalation. One failed lookup is a typo and gets the calm
   *  checklist; two in a row means typing is not working, and the page stops
   *  asking for more of it. Counted by the CALLER in a plain variable, never
   *  in storage: this is a within-visit judgement, not a profile. */
  function failureEscalation(attempts) {
    var n = typeof attempts === 'number' && isFinite(attempts) ? attempts : 0;
    return n >= 2 ? 'loud' : 'calm';
  }

  function phoneRenderModel(prefix) {
    return {
      kind: 'phone',
      prefix: prefix,
      message: COPY.phone_order_prefix,
      /* The whole point of grill Q2: the caller makes NO request to the lookup
       * endpoint on this branch. The GA4 event still fires, which is why the
       * browser test asserts zero LOOKUP requests rather than zero requests. */
      shouldLookup: false,
    };
  }

  function failureModel(reason) {
    var known = COPY[reason] && ['no_match', 'rate_limited', 'challenge_failed', 'unavailable', 'bad_request'].indexOf(reason) !== -1;
    return {
      kind: 'failure',
      reason: known ? reason : 'unavailable',
      message: known ? COPY[reason] : COPY.unavailable,
    };
  }

  /** currentYear is optional and only reaches formatDayShort, which appends a
   *  year to a shipped date that falls outside it. The controller passes the
   *  browser's year; a caller that passes nothing gets bare "Jul 30". */
  function buildRenderModel(payload, currentYear) {
    if (!payload || typeof payload !== 'object') return failureModel('unavailable');
    if (payload.ok !== true) return failureModel(String(payload.reason || ''));
    var order = payload.order;
    if (!order || typeof order !== 'object') return failureModel('unavailable');

    var notices = [];
    if (order.items_truncated === true) notices.push(COPY.items_truncated);
    if (order.shipments_truncated === true) notices.push(COPY.shipments_truncated);

    var shipments = (Array.isArray(order.shipments) ? order.shipments : []).map(function (s) {
      var rows = tracking(s && s.tracking);
      var numbered = [];
      var carriers = [];
      rows.forEach(function (row) {
        if (row.number) numbered.push(row);
        if (row.carrier && carriers.indexOf(row.carrier) === -1) carriers.push(row.carrier);
      });
      return {
        shippedOnLabel: formatDayShort(s && s.shipped_on, currentYear),
        // Chips in the shipment head. One per distinct carrier, in order.
        carriers: carriers,
        tracking: numbered,
        // Shipped, but nothing to click yet. The card says so rather than
        // showing an empty space where a button belongs.
        trackingPending: numbered.length === 0,
        note: s && s.tracking_truncated === true ? COPY.tracking_truncated : null,
        items: items(s && s.items),
      };
    });

    var model = {
      kind: 'order',
      number: typeof order.number === 'string' ? order.number : '',
      placedOnLabel: formatDay(order.placed_on),
      status: typeof order.status === 'string' ? order.status : '',
      statusLabel: typeof order.status_label === 'string' ? order.status_label : '',
      notices: notices,
      shipments: shipments,
      shipmentsTruncated: order.shipments_truncated === true,
      notYetShipped: items(order.not_yet_shipped),
      /* When shipments are truncated the remainder is NOT complete and the
       * page must not present it as if it were (case 20). Truthful, not
       * silent: a truncated shipment list can otherwise show an item as not
       * yet shipped when it shipped. */
      notYetShippedNote: order.shipments_truncated === true ? COPY.not_yet_shipped_incomplete : null,
      agingNudge: order.aging_nudge === true,
    };
    // Computed last: the sentence reads the finished model, so there is one
    // definition of "how many shipments" rather than two.
    model.answer = answerSentence(model);
    return model;
  }

  return {
    COPY: COPY,
    PHONE: PHONE,
    PHONE_HREF: PHONE_HREF,
    EMAIL: EMAIL,
    HOURS: HOURS,
    PREFILL_MATCH_SOURCE: PREFILL_MATCH_SOURCE,
    PREFILL_PATTERN_SOURCE: PREFILL_PATTERN_SOURCE,
    normalizeOrderNumber: normalizeOrderNumber,
    classifyOrderNumber: classifyOrderNumber,
    isValidEmailShape: isValidEmailShape,
    readPrefillParam: readPrefillParam,
    formatDay: formatDay,
    formatDayShort: formatDayShort,
    normalizeCarrier: normalizeCarrier,
    carrierTrackingUrl: carrierTrackingUrl,
    answerSentence: answerSentence,
    failureEscalation: failureEscalation,
    phoneRenderModel: phoneRenderModel,
    buildRenderModel: buildRenderModel,
  };
});
